import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getOwnBusiness } from '../api/endpoints/businesses';
import { patchBusinessProfile } from '../api/endpoints/profile';
import { getAuthMethods, startGoogleAuth } from '../api/endpoints/signup';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { BusinessDetail } from '../api/schemas';
import { useSession } from '../auth/useSession';
import { ConfirmAction } from '../components/ConfirmAction';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { MapPicker } from '../components/MapPicker';
import { NotificationDestinationCard } from '../components/NotificationDestinationCard';
import { PageHeader } from '../components/PageHeader';
import { PhotoManager } from '../components/PhotoManager';
import { MerchantReadiness } from '../components/MerchantReadiness';
import { getOnboarding } from '../api/endpoints/onboarding';
import { countryOfZone, isZonedCountry, zonesFor } from '../lib/country';
import { useMutationAttempt } from '../lib/use-mutation-attempt';

const PRICE_LEVELS = [1, 2, 3] as const;

type ProfileForm = {
  name: string;
  tagline: string;
  emoji: string;
  neighborhood: string;
  priceLevel: 1 | 2 | 3;
  cuisine: string;
  address: string;
  lat: number | null;
  lng: number | null;
};

function toForm(business: BusinessDetail): ProfileForm {
  return {
    name: business.name,
    tagline: business.tagline,
    emoji: business.emoji,
    neighborhood: business.neighborhood,
    priceLevel: business.priceLevel,
    cuisine: business.cuisine ?? '',
    address: business.address ?? '',
    lat: business.lat ?? null,
    lng: business.lng ?? null,
  };
}

export function NegocioPage() {
  const { session } = useSession();
  return <NegocioEditor key={session?.businessId ?? ''} />;
}

function NegocioEditor() {
  const { session } = useSession();
  const businessId = session?.businessId ?? '';
  const queryClient = useQueryClient();

  const businessQuery = useQuery({
    queryKey: ['business-detail', businessId],
    queryFn: getOwnBusiness,
    enabled: Boolean(businessId),
  });

  const [form, setForm] = useState<ProfileForm | undefined>();

  useEffect(() => {
    if (businessQuery.data) { const loaded = toForm(businessQuery.data); setForm(current => current ?? loaded); }
  }, [businessQuery.data]);
  // Its stored country, else the country of its zone (businesses from before 2 Nov 2026).
  const businessCountry = businessQuery.data?.countryCode ?? countryOfZone(form?.neighborhood ?? '');

  const saveMutation = useMutationAttempt({
    mutationFn: (input: ProfileForm, key: string) =>
      patchBusinessProfile(
        {
          name: input.name,
          tagline: input.tagline,
          emoji: input.emoji,
          neighborhood: input.neighborhood,
          priceLevel: input.priceLevel,
          address: input.address || null,
          lat: input.lat,
          lng: input.lng,
          ...(businessQuery.data?.category === 'restaurant' ? { cuisine: input.cuisine || null } : {}),
        },
        key,
      ),
    onSuccess: (_updated, input) => {
      setForm(input);
      void queryClient.invalidateQueries({ queryKey: ['business-detail', businessId] });
      void queryClient.invalidateQueries({ queryKey: ['business-onboarding'] });
    },
  });

  return (
    <main>
      <PageHeader title="Negocio" description="El perfil y las fotos que ven tus clientes." />
      {businessQuery.data ? (
        <DisponibilidadSection business={businessQuery.data} businessId={businessId} />
      ) : null}
      <NotificationDestinationCard />
      <AccesoSection />
      {businessQuery.isLoading ? <LoadingState label="Cargando negocio…" /> : null}
      {businessQuery.isError ? <ErrorBanner message={errorMessage(businessQuery.error)} /> : null}
      {saveMutation.isError ? <ErrorBanner message={errorMessage(saveMutation.error)} /> : null}
      {saveMutation.isSuccess ? (
        <p role="status" className="notice">
          Perfil guardado.
        </p>
      ) : null}

      {form ? (
        <section className="section" aria-label="Perfil">
          <h2 className="section-label">Perfil</h2>
          <form
            className="card form-grid"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (saveMutation.uncertain) saveMutation.retry();
              else saveMutation.mutate(form);
            }}
          >
            <FormField label="Nombre" htmlFor="business-name">
              <input
                id="business-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </FormField>
            <FormField label="Frase corta" htmlFor="business-tagline">
              <input
                id="business-tagline"
                value={form.tagline}
                onChange={(event) => setForm({ ...form, tagline: event.target.value })}
                required
              />
            </FormField>
            <FormField label="Emoji" htmlFor="business-emoji">
              <input
                id="business-emoji"
                value={form.emoji}
                onChange={(event) => setForm({ ...form, emoji: event.target.value })}
                maxLength={8}
                required
              />
            </FormField>
            {isZonedCountry(businessCountry) ? (
              <FormField label="Zona" htmlFor="business-neighborhood">
                <select
                  id="business-neighborhood"
                  value={form.neighborhood}
                  onChange={(event) => setForm({ ...form, neighborhood: event.target.value })}
                >
                  {/* Only the zones of the business's own country: a Caracas business never lists Panama zones. */}
                  {zonesFor(businessCountry).map((neighborhood) => (
                    <option key={neighborhood} value={neighborhood}>
                      {neighborhood}
                    </option>
                  ))}
                </select>
              </FormField>
            ) : (
              // Abroad (2 Nov 2026) there are no Dale zones: the business keeps its own city or area.
              <FormField label="Ciudad o zona" htmlFor="business-neighborhood">
                <input
                  id="business-neighborhood"
                  value={form.neighborhood}
                  maxLength={80}
                  required
                  onChange={(event) => setForm({ ...form, neighborhood: event.target.value })}
                />
              </FormField>
            )}
            <FormField label="Nivel de precio" htmlFor="business-price-level">
              <select
                id="business-price-level"
                value={form.priceLevel}
                onChange={(event) => setForm({ ...form, priceLevel: Number(event.target.value) as 1 | 2 | 3 })}
              >
                {PRICE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {'$'.repeat(level)}
                  </option>
                ))}
              </select>
            </FormField>
            {businessQuery.data?.category === 'restaurant' ? (
              <FormField label="Cocina" htmlFor="business-cuisine">
                <input
                  id="business-cuisine"
                  value={form.cuisine}
                  onChange={(event) => setForm({ ...form, cuisine: event.target.value })}
                />
              </FormField>
            ) : null}
            <FormField label="Dirección" htmlFor="business-address" className="span-2">
              <input
                id="business-address"
                value={form.address}
                onChange={(event) => setForm({ ...form, address: event.target.value })}
              />
            </FormField>
            <FormField label="Ubicación" htmlFor="business-map" className="span-2">
              <MapPicker
                lat={form.lat}
                lng={form.lng}
                neighborhood={form.neighborhood}
                country={businessCountry}
                onChange={(lat, lng) => setForm({ ...form, lat, lng })}
                onClear={() => setForm({ ...form, lat: null, lng: null })}
                onAddress={(address) => setForm((prev) => (prev ? { ...prev, address } : prev))}
              />
            </FormField>
            <button type={saveMutation.uncertain ? "button" : "submit"} onClick={saveMutation.uncertain ? saveMutation.retry : undefined} className="btn" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Guardando…' : saveMutation.uncertain ? 'Reintentar perfil' : 'Guardar perfil'}
            </button>
          </form>
        </section>
      ) : null}

      {businessQuery.data ? (
        <PhotoManager businessId={businessId} photos={businessQuery.data.photos ?? []} />
      ) : null}
      <MerchantReadiness />
    </main>
  );
}

/**
 * Linking a Google account to this acceso. The link itself is a full-page
 * redirect through Google, so this is a button and a status line, not a form:
 * whether it worked comes back as `?google=vinculado` on the way in.
 */
function AccesoSection() {
  const [googleOpen, setGoogleOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAuthMethods()
      .then((methods) => {
        if (!cancelled) setGoogleOpen(methods.data.google);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!googleOpen) return null;
  const linked = new URLSearchParams(window.location.search).get('google') === 'vinculado';

  return (
    <section className="section" aria-label="Acceso">
      <h2 className="section-label">Acceso</h2>
      <div className="card row">
        <div className="grow">
          <p className="row-title">Entrar con Google</p>
          <p className="row-sub">
            {linked
              ? 'Listo: ya puedes entrar con tu cuenta de Google.'
              : 'Vincula tu cuenta de Google y entra sin escribir la contraseña.'}
          </p>
        </div>
        {linked ? null : (
          <button
            type="button"
            className="btn small secondary"
            onClick={() => startGoogleAuth('link')}
          >
            Vincular Google
          </button>
        )}
      </div>
    </section>
  );
}

type DisponibilidadSectionProps = { business: BusinessDetail; businessId: string };

/**
 * "Cerrado temporalmente": the switch that takes the business out of the app
 * without touching anything else. A platform suspension shows here too, as a
 * banner with no control: only Dale can lift that one.
 */
function DisponibilidadSection({ business, businessId }: DisponibilidadSectionProps) {
  const queryClient = useQueryClient();
  const pauseKey = useRef(createIdempotencyKeyHolder()).current;
  const isPaused = business.isPaused === true;
  const onboarding = useQuery({ queryKey: ['business-onboarding'], queryFn: getOnboarding });
  const published = onboarding.data?.state === 'published' && onboarding.data.ready;

  const pauseMutation = useMutation({
    mutationFn: (nextPaused: boolean) =>
      patchBusinessProfile({ isPaused: nextPaused }, pauseKey.ensure()),
    onSuccess: (updated) => {
      // Write the 200 into the cache rather than refetching: an immediate
      // re-read can ride Hyperdrive's stale window and answer with the old
      // value, flipping the card back (same reason the admin Locales list
      // writes its 201 directly). Photos are not in the patch response, so
      // the previous detail keeps them.
      queryClient.setQueryData<BusinessDetail>(['business-detail', businessId], (previous) =>
        previous
          ? {
              ...previous,
              ...updated,
              // The API omits these flags entirely when false, so a plain
              // spread could never clear one: absent has to mean false here.
              isPaused: updated.isPaused === true,
              isSuspended: updated.isSuspended === true,
              photos: previous.photos,
            }
          : previous,
      );
    },
    onSettled: () => pauseKey.reset(),
  });

  if (business.isSuspended) {
    return (
      <section className="section" aria-label="Disponibilidad">
        <h2 className="section-label">Disponibilidad</h2>
        <div className="card">
          <p className="status" data-tone="due">
            Suspendido
          </p>
          <p className="row-sub">
            Dale suspendió este negocio, así que no aparece en la app ni recibe reservas.
            Escríbenos para reactivarlo.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="section" aria-label="Disponibilidad">
      <h2 className="section-label">Disponibilidad</h2>
      <div className="card row">
        <div className="grow">
          <p className="row-title">{isPaused ? 'Cerrado temporalmente' : published ? 'Abierto en la app' : onboarding.data ? 'Activo, sin publicar' : 'Revisando publicación'}</p>
          <p className="row-sub">
            {isPaused
              ? 'No apareces en la app y no puedes recibir reservas nuevas. Tus reservas ya hechas siguen ahí.'
              : published ? 'Apareces en la app y puedes recibir reservas.' : 'Revisa Primer día para completar la publicación.'}
          </p>
          {onboarding.isError ? <ErrorBanner message="No pudimos verificar la publicación." /> : null}
          {pauseMutation.isError ? <ErrorBanner message={errorMessage(pauseMutation.error)} /> : null}
        </div>
        {isPaused ? (
          <button
            type="button"
            className="btn small"
            disabled={pauseMutation.isPending}
            onClick={() => pauseMutation.mutate(false)}
          >
            {pauseMutation.isPending ? 'Abriendo…' : 'Volver a abrir'}
          </button>
        ) : (
          <ConfirmAction
            label="Cerrar temporalmente"
            variant="secondary"
            pending={pauseMutation.isPending}
            onConfirm={() => pauseMutation.mutate(true)}
          />
        )}
      </div>
    </section>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
